/**
 * DepthCapture.js — scene depth + view normals for the FX system.
 * owner: fx
 *
 * Soft particles, screen-space particle collision and projected decals all need
 * to know what geometry is behind a pixel. The render team will eventually own
 * this buffer; until then FX renders its own.
 *
 * FEATURE DETECTION (checked every frame, in priority order):
 *   1. ctx.render.passes.prepass      — the render team's G-buffer.
 *        gNormal.rgb = view-space normal, gData.b = view-space distance (m).
 *        This is the fast path: zero extra draw calls.
 *   2. ctx.render.depthNormalTexture  — rgb = view normal, a = view-space depth
 *   3. our own prepass                — one extra scene traversal
 *
 * Consumers see a uniform interface:
 *   mode 0  tDepth  packed RGBA (rgb = view normal, a = viewZ)
 *   mode 1  tDepth = gData (b = viewZ), tNormal = gNormal (rgb = view normal)
 * and use the fxSceneZ()/fxSceneN() helpers below, which return FAR_SENTINEL
 * where nothing was drawn.
 *
 * See src/fx/RENDER_HANDOFF.md.
 */
import * as THREE from 'three';
import { FullScreenQuad } from 'three/examples/jsm/postprocessing/Pass.js';

const FAR_SENTINEL = 1.0e6;

const PREPASS_VS = /* glsl */`
varying vec3 vViewNormal;
varying float vViewZ;
#include <common>
#include <skinning_pars_vertex>
#include <morphtarget_pars_vertex>
void main(){
  #include <beginnormal_vertex>
  #include <morphnormal_vertex>
  #include <skinbase_vertex>
  #include <skinnormal_vertex>
  #include <defaultnormal_vertex>
  vViewNormal = normalize(transformedNormal);
  #include <begin_vertex>
  #include <morphtarget_vertex>
  #include <skinning_vertex>
  #include <project_vertex>
  vViewZ = -mvPosition.z;
}
`;

const PREPASS_FS = /* glsl */`
varying vec3 vViewNormal;
varying float vViewZ;
void main(){
  vec3 n = normalize(vViewNormal);
  if (!gl_FrontFacing) n = -n;
  gl_FragColor = vec4(n, vViewZ);
}
`;

export class DepthCapture {
  constructor(renderer) {
    this.renderer = renderer;
    this.enabled = true;
    this.scale = 1.0;
    this.width = 2;
    this.height = 2;
    this.external = false;
    this.drawCalls = 0;

    this.target = new THREE.WebGLRenderTarget(2, 2, {
      type: THREE.HalfFloatType,
      format: THREE.RGBAFormat,
      minFilter: THREE.NearestFilter,
      magFilter: THREE.NearestFilter,
      depthBuffer: true,
      stencilBuffer: false,
      generateMipmaps: false,
      colorSpace: THREE.NoColorSpace,
    });

    this.material = new THREE.ShaderMaterial({
      vertexShader: PREPASS_VS,
      fragmentShader: PREPASS_FS,
      side: THREE.FrontSide,
      toneMapped: false,
      fog: false,
    });

    // Clearing via a quad instead of renderer.setClearColor keeps clear-state
    // ownership with PostFX (CONTRACTS.md non-negotiable #4).
    this._clearQuad = new FullScreenQuad(new THREE.ShaderMaterial({
      uniforms: { uFar: { value: FAR_SENTINEL } },
      vertexShader: 'void main(){ gl_Position = vec4(position.xy, 0.0, 1.0); }',
      fragmentShader: 'uniform float uFar; void main(){ gl_FragColor = vec4(0.0, 0.0, 1.0, uFar); }',
      depthTest: false, depthWrite: false, toneMapped: false,
    }));

    /** Objects hidden during the pass (our own FX geometry). */
    this.exclude = [];
  }

  /** @returns {THREE.Texture|null} the texture FX shaders should sample. */
  get texture() {
    return this._activeTexture || null;
  }

  /** @returns {THREE.Texture|null} separate normal texture (mode 1 only). */
  get normalTexture() {
    return this._activeNormal || null;
  }

  /** 0 = packed RGBA, 1 = render team's split gNormal/gData G-buffer. */
  get mode() {
    return this._mode || 0;
  }

  resize(w, h) {
    const W = Math.max(2, Math.floor(w * this.scale));
    const H = Math.max(2, Math.floor(h * this.scale));
    if (W === this.width && H === this.height) return;
    this.width = W; this.height = H;
    this.target.setSize(W, H);
  }

  /** Called once per frame before anything samples the depth buffer. */
  render(ctx) {
    this.drawCalls = 0;

    // 1. the render team's G-buffer — preferred, free
    const pre = ctx.render?.passes?.prepass;
    if (pre && pre.rt && pre.dataTexture?.isTexture && pre.normalTexture?.isTexture) {
      this.external = true;
      this._mode = 1;
      this._activeTexture = pre.dataTexture;
      this._activeNormal = pre.normalTexture;
      this.width = pre.width; this.height = pre.height;
      return;
    }

    // 2. a packed depth+normal texture published directly
    const ext = ctx.render?.depthNormalTexture || ctx.render?.gbuffer?.normalDepth || null;
    if (ext && ext.isTexture) {
      this.external = true;
      this._mode = 0;
      this._activeTexture = ext;
      this._activeNormal = null;
      return;
    }

    // 3. our own prepass
    this.external = false;
    this._mode = 0;
    this._activeNormal = null;
    if (!this.enabled) { this._activeTexture = null; return; }

    const r = this.renderer;
    const size = r.getDrawingBufferSize(new THREE.Vector2());
    this.resize(size.x, size.y);

    const scene = ctx.scene;
    const hidden = [];
    for (const o of this.exclude) {
      if (o && o.visible) { o.visible = false; hidden.push(o); }
    }

    const prevTarget = r.getRenderTarget();
    const prevAuto = r.autoClear;
    const prevOverride = scene.overrideMaterial;

    r.setRenderTarget(this.target);
    r.autoClear = false;
    r.clear(false, true, false);          // depth only — colour comes from the quad
    this._clearQuad.render(r);
    scene.overrideMaterial = this.material;
    r.render(scene, ctx.camera);
    scene.overrideMaterial = prevOverride;
    r.setRenderTarget(prevTarget);
    r.autoClear = prevAuto;

    for (const o of hidden) o.visible = true;
    this._activeTexture = this.target.texture;
    this.drawCalls = 1 + r.info.render.calls; // informational only
  }

  dispose() {
    this.target.dispose();
    this.material.dispose();
    this._clearQuad.material.dispose();
    this._clearQuad.dispose();
  }
}

export { FAR_SENTINEL };
